import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import type { PickedElement } from "@/lib/requests/element-picker";
import { summarizePick } from "@/lib/requests/element-picker";
import { enqueue, isRetryable, type RequestPayload } from "@/lib/requests/queue";
import { submitRequest } from "@/lib/requests/submit";
import type { RequestContext } from "@/lib/requests/context";

export class OfflineError extends Error {
  constructor() {
    super("offline");
    this.name = "OfflineError";
  }
}

export class HeldOfflineError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "HeldOfflineError";
  }
}

export interface RequestSubmitInput {
  message: string;
  pick: PickedElement | null;
  context: RequestContext;
  pageName: string;
}

function buildPayload(id: string, capturedAt: string, input: RequestSubmitInput): RequestPayload {
  return {
    id,
    message: input.message,
    page_path: input.context.page_path,
    page_url: input.context.page_url,
    page_name: input.pageName,
    element: input.pick
      ? {
          selector: input.pick.selector,
          text: input.pick.text,
          role: input.pick.role,
          label: input.pick.label,
          testid: input.pick.testId,
          rect: input.pick.rect,
          pointer: input.pick.pointer,
          confident: input.pick.confident,
        }
      : undefined,
    viewport: input.context.viewport,
    device_type: input.context.device_type,
    dialog_title: input.context.dialog_title,
    user_agent: input.context.user_agent,
    captured_at: capturedAt,
    // NOTE: no `source` field — the server derives it.
  };
}

/**
 * `holdOffline` (default true) parks a retryable failure in the local queue. Only
 * pass true where useRequestQueue is mounted to flush it — today that is the
 * super-admin-only RequestButton — or the request sits on the device forever (#567).
 */
export function useRequestSubmit({ holdOffline = true }: { holdOffline?: boolean } = {}) {
  const { user, session } = useAuth();

  return useMutation<void, Error, RequestSubmitInput>({
    networkMode: "always", // the default "online" mode never calls mutationFn at all offline
    retry: false,
    mutationFn: async (input) => {
      const id = crypto.randomUUID(); // client-generated -> replay can't double-write
      const captured_at = new Date().toISOString();
      const payload = buildPayload(id, captured_at, input);
      try {
        if (navigator.onLine === false) throw new OfflineError();
        await submitRequest(payload, session?.access_token ?? null);
      } catch (err) {
        if (!holdOffline || !isRetryable(err)) throw err;
        if (!enqueue(payload, user?.id ?? null)) throw err;
        throw new HeldOfflineError("No connection — held on this device.");
      }
    },
    onSuccess: (_data, input) => {
      toast.success("Request sent", {
        description: input.pick ? summarizePick(input.pick) : input.context.page_path,
      });
    },
    onError: (err, _input) => {
      if (err instanceof HeldOfflineError) {
        toast.success("Held on this device", {
          description: "It sends itself when you're back online.",
        });
        return;
      }
      toast.error("Not sent — your text is still here", {
        description: err.message,
        duration: Infinity,
      });
    },
  });
}
